import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PanelModule } from 'primeng/panel';

/**
 * Case Information Component
 * 
 * A reusable component for capturing court and case details across different order types.
 * This component manages case number, case name, judge, court, jurisdiction, and address information.
 */
@Component({
  selector: 'app-case-information',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    PanelModule
  ],
  templateUrl: './case-information.component.html',
  styleUrl: './case-information.component.scss'
})
export class CaseInformationComponent implements OnInit {
  /**
   * Parent form group containing case information controls
   */
  @Input() formGroup!: FormGroup;

  /**
   * Array of available states for the state dropdown
   */
  @Input() states: any[] = [];

  /**
   * Optional prefix for field IDs to ensure uniqueness when multiple instances exist
   * Default: 'case-info'
   */
  @Input() idPrefix: string = 'case-info';

  /**
   * Whether to show the component in read-only mode
   */
  @Input() isReadOnly: boolean = false;

  /**
   * Optional header text for the panel
   * Default: 'Court details'
   */
  @Input() headerText: string = 'Court details';

  /**
   * Whether to show the panel wrapper
   * Default: true
   */
  @Input() showPanel: boolean = true;

  /**
   * Emits when any form value changes
   */
  @Output() formChanged = new EventEmitter<void>();

  ngOnInit(): void {
    if (!this.formGroup) {
      throw new Error('formGroup is required for CaseInformationComponent');
    }

    // Subscribe to form changes and emit to parent
    this.formGroup.valueChanges.subscribe(() => {
      this.formChanged.emit();
    });
  }

  /**
   * Checks if a form control has validation errors and should display error state
   * @param controlName The name of the form control to check
   * @returns True if the control is invalid and has been touched or is dirty
   */
  isError(controlName: string): boolean {
    const control = this.formGroup.get(controlName);
    return control ? (control.invalid && (control.dirty || control.touched)) : false;
  }

  /**
   * Gets the error message for a specific control
   * @param controlName The name of the form control
   * @returns The appropriate error message
   */
  getErrorMessage(controlName: string): string {
    const control = this.formGroup.get(controlName);
    if (control?.hasError('required')) {
      return 'This field is mandatory';
    }
    return '';
  }
}

